import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class ClientI18nService {
  private readonly basePath: string = '../xmlFiles/i18n/';
  private readonly combinedFileName: string = 'CustomTranslation.json';

  // Cache of loaded translation dictionaries per file key
  private languageKeyToDictionary: Map<string, Record<string, string>> = new Map();
  private languageKeyToLoadingPromise: Map<string, Promise<void>> = new Map();
  // Combined dictionary: key -> (string | { [lang]: string })
  private combinedDictionary: Record<string, any> | null = null;
  private combinedLoadingPromise: Promise<void> | null = null;

  constructor(private httpClient: HttpClient) {}

  // New API: load single combined dictionary (CustomTranslation.json)
  public async ensureCombinedDictionaryLoaded(): Promise<void> {
    if (this.combinedDictionary) return;
    if (this.combinedLoadingPromise) {
      await this.combinedLoadingPromise; return;
    }
    this.combinedLoadingPromise = (async () => {
      try {
        const dict = await this.httpClient
          .get<Record<string, any>>(`${this.basePath}${this.combinedFileName}`)
          .toPromise();
        if (dict && typeof dict === 'object') {
          this.combinedDictionary = dict;
        }
      } catch {
        // If not found, we silently proceed; fallback per-language files may exist
        this.combinedDictionary = null;
      }
    })();
    try {
      await this.combinedLoadingPromise;
    } finally {
      this.combinedLoadingPromise = null;
    }
  }

  public isCombinedLoaded(): boolean {
    return !!this.combinedDictionary;
  }

  public hasAnyDictionaryForLanguage(languageCode: string): boolean {
    return this.getFirstCachedDictionaryForLanguage(languageCode) != null;
  }

  public async loadDictionariesForLanguage(languageCode: string): Promise<void> {
    const candidateKeys = this.generateCandidateLanguageKeys(languageCode);

    // If any candidate is already loaded, nothing else to do
    if (candidateKeys.some((key) => this.languageKeyToDictionary.has(key))) {
      return;
    }

    // If a load is already in progress for any candidate, await it
    for (const key of candidateKeys) {
      const inFlight = this.languageKeyToLoadingPromise.get(key);
      if (inFlight) {
        await inFlight;
        return;
      }
    }

    // Attempt candidates in order until one succeeds
    const loadPromise = (async () => {
      for (const key of candidateKeys) {
        try {
          const dict = await this.httpClient
            .get<Record<string, any>>(`${this.basePath}${this.combinedFileName}`)
            .toPromise();
          if (dict) {
            const langDict = this.extractLanguageDictFromCombined(dict, key);
            if (langDict) {
              this.languageKeyToDictionary.set(key, langDict);
              break;
            }
          }
        } catch {
          // Try next candidate silently
        }
      }
    })();

    // Track the load attempt on the primary language key
    this.languageKeyToLoadingPromise.set(candidateKeys[0], loadPromise);
    try {
      await loadPromise;
    } finally {
      this.languageKeyToLoadingPromise.delete(candidateKeys[0]);
    }
  }

  private extractLanguageDictFromCombined(
    combined: Record<string, any>,
    languageCode: string
  ): Record<string, string> | null {
    if (!combined || typeof combined !== 'object') return null;
    const candidates = this.generateCandidateLanguageKeys(languageCode);
    for (const code of candidates) {
      const langDict =
        combined[code] ||
        combined[code?.toLowerCase?.()] ||
        combined[code?.toUpperCase?.()] ||
        combined[code?.replace?.('-', '_')] ||
        combined[code?.replace?.('_', '-')];
      if (langDict && typeof langDict === 'object') {
        return langDict as Record<string, string>;
      }
    }
    const en = combined['en'] || combined['EN'];
    if (en && typeof en === 'object') return en as Record<string, string>;
    return null;
  }

  public translate(languageCode: string, key: string): string | null {
    // First try combined dictionary if present (language-first structure)
    if (this.combinedDictionary) {
      const candidates = this.generateCandidateLanguageKeys(languageCode);
      for (const code of candidates) {
        const langDict =
          (this.combinedDictionary[code]) ||
          (this.combinedDictionary[code?.toLowerCase?.()]) ||
          (this.combinedDictionary[code?.toUpperCase?.()]) ||
          (this.combinedDictionary[code?.replace?.('-', '_')]) ||
          (this.combinedDictionary[code?.replace?.('_', '-')]);
        if (langDict && typeof langDict === 'object') {
          const value = langDict[key];
          if (typeof value === 'string') return value;
        }
      }
      // Explicit fallback to English
      const enDict = this.combinedDictionary['en'] || this.combinedDictionary['EN'];
      if (enDict && typeof enDict === 'object' && typeof enDict[key] === 'string') {
        return enDict[key];
      }
    }
    // Fallback to per-language dictionaries
    const dict = this.getFirstCachedDictionaryForLanguage(languageCode);
    if (!dict) return null;
    return Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : null;
  }

  private getFirstCachedDictionaryForLanguage(languageCode: string): Record<string, string> | null {
    const candidates = this.generateCandidateLanguageKeys(languageCode.toLocaleLowerCase());
    for (const key of candidates) {
      const dict = this.languageKeyToDictionary.get(key.toLocaleLowerCase());
      if (dict) return dict;
    }
    return null;
  }

  private generateCandidateLanguageKeys(languageCode: string): string[] {
    if (!languageCode) return ['en'];
    const lc = languageCode.toLowerCase();
    const parts = lc.split('-');
    const base = parts[0];
    const candidates = new Set<string>();

    // try full code as-is, lowercase, and base language
    candidates.add(languageCode.toLocaleLowerCase());
    candidates.add(lc);
    candidates.add(base);
    return Array.from(candidates);
  }

  private pickBestLanguageValue(map: Record<string, string>, languageCode: string): string | null {
    if (!languageCode) languageCode = 'en';
    const candidates: string[] = [];
    const lang = languageCode;
    const lc = languageCode.toLowerCase();
    const baseDash = lc.split('-')[0];
    const baseUnd = lc.split('_')[0];
    // Exact and case-insensitive variants
    candidates.push(lang);
    candidates.push(lc);
    candidates.push(lc.replace('-', '_'));
    candidates.push(lc.replace('_', '-'));
    candidates.push(baseDash);
    candidates.push(baseUnd);
    // Fallbacks
    candidates.push('en');
    for (const code of candidates) {
      const hit = map[code] ?? map[code.toLowerCase()] ?? map[code.toUpperCase()];
      if (typeof hit === 'string') return hit;
    }
    return null;
  }
}


